// src/com/rtd_term_pos/RtdTermPos.js
import React, { useEffect, useRef, useState } from 'react';
//import { styled } from '@mui/material/styles';
import { IconButton, CircularProgress, Button, Menu, MenuItem } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NearMeIcon from '@mui/icons-material/NearMe'; // 지도로 이동 (지정 차량)
import LocationOnIcon from '@mui/icons-material/LocationOn'; // 지도로 이동
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'; // 위치보고 지연 차량 필터링
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {ContentConainer, ContentHeader, ControlBox, ToolBox} from '../../common/TopViewControl';
import { AppObject, AppStyle, AppWord, MapValue } from '../../value/AppConst';
import { rtdTermPosListRepo, rtdTermPosListNoReportRepo, userInfoRepo } from '../../value/AppModel';
import { RtdTermPosDictionary } from './RtdTermPosDict';
import { useAlertSetter } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import ValueMisc from '../../value/ValueMisc';
import { LIST_RTD_TERM_POS, LIST_RTD_TERM_POS_NO_REPORT } from './RtdTermPosGql';
import RtdTermPosNoReport from './RtdTermPosNoReport';

function getDuplicationCheckingData(list) {

    // comId, custId 다른 경우 지도에 표시하지 않으므로 지도 넘어갈 때 지도에 나타나는 것을 찾아 갈 수 있게 미리 준비.
    // 운송사는 하지 않아도 됨.
    const duplication = {};
    const vehiclesOnMap = {};
    const posMap = {}; // 이름과 위치로 같은 차량임을 판단.
    list.forEach((v) => {
        if(v.comId === v.custId) {
            vehiclesOnMap[v.vehInfoSeq] = v;
            posMap[`${v.vehPlates}-${v.lat}-${v.lon}`] = v.vehInfoSeq;
        }
    });
    // again to find duplicate
    list.forEach((v) =>{
        if(v.comId !== v.custId) {
            const key = `${v.vehPlates}-${v.lat}-${v.lon}`;
            if(posMap[key]) {
                duplication[v.vehInfoSeq] = posMap[key];
            }
        }
    });
    return {duplication, vehiclesOnMap};
}
/**
 * (RTD_TERM_POS) 정보 View Controller
 * 차량별 최종 위치
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-11-29 16:42:42 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function RtdTermPos() {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const rtdTermPosListData = useReactiveVar(rtdTermPosListRepo);
    const rtdTermPosListNoReportData = useReactiveVar(rtdTermPosListNoReportRepo);
    const [searchText, setSearchText] = useState('');
    const [anchorElForFilter, setAnchorElForFilter] = useState(null);
    const [openNoReportListBox, setOpenNoReportListBox] = useState(false);
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    const duplicateRef = useRef({}); // 지도에 나타나지 않는 차량에 나타나는 차량 매핑.
    const mapVehRef = useRef({}); // 지도에 나타나는 차량만 모음.

    // ##### Call GraphQL to get List #####
    const [getRtdTermPosList, responseList] = useLazyQuery(LIST_RTD_TERM_POS, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: RtdTermPosDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getRtdTermPosNoReportList, responseNoReportList] = useLazyQuery(LIST_RTD_TERM_POS_NO_REPORT, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetListNoReport(data, option)},
        onError: (error) => addAlert({title: '보고누락 차량 조회 오류', message: [error.message]})
    });
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###

    const getRtdTermPosListNow = () => {
        getRtdTermPosList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        const diff = new Date().getTime()/1000 - rtdTermPosListData.when;
        if(diff > 60)
            getRtdTermPosListNow();
        else if(!sessionInfo.user.isTransCompanyUser()) {
            // comId, custId 다른 경우 지도에 표시하지 않으므로 지도 넘어갈 때 지도에 나타나는 것을 찾아 갈 수 있게 미리 준비.
            // 운송사는 하지 않아도 됨.
            const {duplication, vehiclesOnMap} = getDuplicationCheckingData(rtdTermPosListData.records);
            duplicateRef.current = duplication;
            mapVehRef.current = vehiclesOnMap;
        }
    }, []);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.rtdTermPosList) {
            rtdTermPosListRepo({when:new Date().getTime()/1000, records:data.rtdTermPosList});
            if(!sessionInfo.user.isTransCompanyUser()) {
                // comId, custId 다른 경우 지도에 표시하지 않으므로 지도 넘어갈 때 지도에 나타나는 것을 찾아 갈 수 있게 미리 준비.
                // 운송사는 하지 않아도 됨.
                const {duplication, vehiclesOnMap} = getDuplicationCheckingData(data.rtdTermPosList);
                duplicateRef.current = duplication;
                mapVehRef.current = vehiclesOnMap;
            }
        }
    };

    const onCompleteGetListNoReport = (data, clientOption) => {
        if(data.rtdTermPosListNoReport) {
            const newData = {...rtdTermPosListNoReportData};
            newData.when = new Date().getTime()/1000;
            newData.records = data.rtdTermPosListNoReport;
            rtdTermPosListNoReportRepo(newData); // Keep daysAgo value as it is.
            // show it
            setOpenNoReportListBox(true);
        }
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<
    const onClickGetPosListNoReport = (daysAgo) => {
        setAnchorElForFilter(null);
        if(daysAgo === rtdTermPosListNoReportData.daysAgo
            && rtdTermPosListNoReportData.records.length > 0
            && new Date().getTime()/1000 - rtdTermPosListNoReportData.when < 300) {
            // show the same list
            setOpenNoReportListBox(true);
            return;
        }
        const newRepoData = {...rtdTermPosListNoReportData, daysAgo, report:[]};
        rtdTermPosListNoReportRepo(newRepoData);
        getRtdTermPosNoReportList({variables:{daysAgo}});
    };

    const onClickOpenMap = () => {
        const mapUrl = ValueMisc.makeMapUrl(); // No parameter
        window.open(mapUrl, AppWord.MAP_WINDOW_NAME);
    };

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else getRtdTermPosListNow();
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onDoubleClickRow = (record) => {
        if(record.lat > MapValue.Korea.South && record.lat < MapValue.Korea.North && record.lon > MapValue.Korea.West && record.lon < MapValue.Korea.East) {
            let veh;
            if(!sessionInfo.user.isTransCompanyUser()) {
                // find real vehInfoSeq
                if(duplicateRef.current[record.vehInfoSeq]) {
                    veh = mapVehRef.current[duplicateRef.current[record.vehInfoSeq]];
                    if(!veh) {
                        enqueueSnackbar('지도에 표시되지 않는 차량입니다.', {variant:'info', autoHideDuration:2000});
                        return;
                    }
                }
                else veh = record;
            }
            else {
                veh = record;
            }


            enqueueSnackbar('지도로 이동합니다.', {variant:'info', autoHideDuration:2000});
            
            const mapUrl = ValueMisc.makeMapUrl({
                    job: MapValue.MapInitEvent.traceVehicle,
                    vehInfoSeqToTrace:veh.vehInfoSeq,
                    vehPlates:veh.vehPlates,
                    vehAlias:veh.vehAlias
                });
            window.open(mapUrl, AppWord.MAP_WINDOW_NAME);
        }
        else {
            addAlert({
                title: '위치 없음',
                message: ['위치정보가 없습니다. 위치정보가 있는 경우만 지도로 이동할 수 있습니다.']
            });
        }
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.TO_MAP: onDoubleClickRow(row); break;
            default: return;
        }
    };

    const tools = [
        {id:AppObject.command.TO_MAP, icon:<NearMeIcon color={AppStyle.mui.secondary}/>, where:(row)=>row.lat > MapValue.Korea.South && row.lat < MapValue.Korea.North && row.lon > MapValue.Korea.West && row.lon < MapValue.Korea.East},
    ];

    const loadingAny
        = responseList.loading;

    return(
        <ContentConainer>
            <ContentHeader>
                <ControlBox>
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    />
                </ControlBox>
                <ToolBox>
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    <Button
                        variant={AppStyle.mui.contained}
                        startIcon={<LocationOnIcon />}
                        onClick={onClickOpenMap}
                        style={{marginLeft:10}}
                    >
                        지도 열기
                    </Button>
                    {
                        !sessionInfo.isStoreUser() &&
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.secondary}
                            startIcon={<HourglassBottomIcon />}
                            onClick={(e)=>{setAnchorElForFilter(e.currentTarget)}}
                            style={{marginLeft:10}}
                        >
                            위치보고 지연 차량 보기
                        </Button>}
                    <Menu
                        open={Boolean(anchorElForFilter)}
                        anchorEl={anchorElForFilter}
                        onClose={()=>setAnchorElForFilter(null)}
                    >
                        <MenuItem onClick={()=>onClickGetPosListNoReport(-3)}>
                            3일 전부터 위치보고 없는 차량
                        </MenuItem>
                        <MenuItem onClick={()=>onClickGetPosListNoReport(-2)}>
                            그제 자정부터 위치보고 없는 차량
                        </MenuItem>
                        <MenuItem onClick={()=>onClickGetPosListNoReport(-1)}>
                            어제 자정부터 위치보고 없는 차량
                        </MenuItem>
                        <MenuItem onClick={()=>onClickGetPosListNoReport(0)}>
                            오늘 내내 위치보고 없는 차량
                        </MenuItem>
                    </Menu>
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={RtdTermPosDictionary.tableProps.tableId}
                tableProps={RtdTermPosDictionary.tableProps}
                records={rtdTermPosListData.records}
                search={searchText}
                tools={tools}
                onClickTool={onClickToolOnRow}
                onRowDoubleClicked={onDoubleClickRow}
            />
            <RtdTermPosNoReport
                open={openNoReportListBox}
                onClose={()=>setOpenNoReportListBox(false)}
            />
        </ContentConainer>
    );
}

/*


                    <Popover
                        open={Boolean(anchorElForFilter)}
                        anchorEl={anchorElForFilter}
                        onClose={()=>setAnchorElForFilter(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <Box style={{padding:10, borderRadius:5, border:AppStyle.BorderCCC}}>
                        </Box>
                    </Popover>
*/